import classnames from 'classnames';
import {useMemo, useState} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import {
  selectNotedMarksInOrder,
  selectSupplementBorderColor,
  selectVisibleRegionAnnotations
} from './annotationSelectors';
import type {Annotation} from './annotationTypes';
import {SUPPLEMENT_BORDER_CLASS_BY_COLOR, SUPPLEMENT_MARKED_CLASS} from './supplementCard';

/**
 * What a supplement's marks make of its card, one mark or many. `regionRef` names the
 * box the edge paints on, held as state because a ref is still null on the mounting
 * pass and nothing re-renders when the marks arrive with it.
 */
export function useSupplementCard(regionId: string): {
  regionRef: (element: HTMLDivElement | null) => void;
  /** The card's box once it has attached; null on the mounting pass. */
  region: HTMLElement | null;
  /** Every mark in the supplement's region, in the order they were written. */
  annotations: Annotation[];
  cardClassName: string;
  /** The marks carrying a note, in the order they read. */
  notedMarks: Annotation[];
} {
  const [region, setRegion] = useState<HTMLElement | null>(null);
  const annotations = useAnnotationModeStore((state) =>
    selectVisibleRegionAnnotations(state, regionId)
  );
  const borderColor = useMemo(() => selectSupplementBorderColor(annotations), [annotations]);
  const notedMarks = useMemo(() => selectNotedMarksInOrder(annotations), [annotations]);

  return {
    regionRef: setRegion,
    region,
    annotations,
    cardClassName: classnames(
      borderColor && [SUPPLEMENT_MARKED_CLASS, SUPPLEMENT_BORDER_CLASS_BY_COLOR[borderColor]]
    ),
    notedMarks
  };
}
